import type { UserManagementAccess } from '../../../../api/staff';
import type { RegionOption } from '../../../../shared/regionAccessOptions';
import type { UserRow } from '../userViewModel';

import DeactivateUserModal from './DeactivateUserModal';
import EditRoleModal from './EditRoleModal';
import InviteUserDrawer from './InviteUserDrawer';
import RevokeInviteModal from './RevokeInviteModal';

export type ActiveDialog =
  | { kind: 'deactivate'; user: UserRow }
  | { kind: 'edit'; user: UserRow }
  | { kind: 'invite'; prefillFrom?: UserRow }
  | { kind: 'revoke-invite'; user: UserRow }
  | null;

interface UsersDialogsProps {
  dialog: ActiveDialog;
  onClose: () => void;
  onMutationSuccess: () => void;
  existingUsers: UserRow[];
  /** The viewer's own user id, so the edit modal can tell a self-edit apart. */
  selfUserId?: string | null;
  /** What the viewer may grant when inviting. */
  userManagement: UserManagementAccess;
  /** Every top-level region; the invite drawer offers the assignable ones. */
  regionOptions: RegionOption[];
}

/** Renders whichever dialog the page currently wants open. Keeps UsersPage
 *  branch-count + line-count under the linter caps. */
export default function UsersDialogs({
  dialog,
  onClose,
  onMutationSuccess,
  existingUsers,
  selfUserId,
  userManagement,
  regionOptions,
}: UsersDialogsProps) {
  if (dialog?.kind === 'deactivate') {
    return (
      <DeactivateUserModal user={dialog.user} onClose={onClose} onDeactivated={onMutationSuccess} />
    );
  }
  if (dialog?.kind === 'edit') {
    return (
      <EditRoleModal
        user={dialog.user}
        isSelf={Boolean(selfUserId) && dialog.user.id === selfUserId}
        onClose={onClose}
        onSaved={onMutationSuccess}
      />
    );
  }
  if (dialog?.kind === 'revoke-invite') {
    return <RevokeInviteModal user={dialog.user} onClose={onClose} onRevoked={onMutationSuccess} />;
  }
  if (dialog?.kind === 'invite') {
    return (
      <InviteUserDrawer
        existingUsers={existingUsers}
        grantableRoles={userManagement.grantableRoles}
        assignableRegionIds={userManagement.assignableRegionIds}
        regionOptions={regionOptions}
        prefillFrom={dialog.prefillFrom}
        onClose={onClose}
        onInvited={onMutationSuccess}
      />
    );
  }
  return null;
}
